import axios from "axios";

// Reference data for the member forms: GET /lookups/document-types,
// GET /lookups/customer-categories and GET /lookups/employees. Any signed-in
// staff user may read them;
// they need no permission, unlike the settings routes (/document-types,
// /customer-categories) that the settings pages use. Each returns the
// company's ACTIVE rows only, as a plain array under `data` (not paginated).
// A record may still hold an inactive type or category: the screens show it
// by the record's own name, not by finding it here.
//
// Every action resolves to that array and rejects with what the global
// interceptor rejects with: the axios response.
//
// `lists` holds the option lists of the member screens (sections/useLookups.js)
// apart from the legacy stores: other screens commit a page or another
// company's rows into those, and the member screens must not show them.

function cleanQuery(params) {
    const query = { ...(params || {}) };
    Object.keys(query).forEach((key) => {
        if (query[key] === null || query[key] === undefined || query[key] === "") {
            delete query[key];
        }
    });
    if (typeof query.is_group === "boolean") {
        query.is_group = query.is_group ? 1 : 0;
    }
    return query;
}

function fetchList(commit, key, url, params) {
    return axios
        .get(url, { params: cleanQuery(params) })
        .then((res) => {
            const rows = Array.isArray(res.data?.data) ? res.data.data : [];
            commit("SET_LIST", { key, rows });
            return rows;
        })
        .catch((err) => Promise.reject(err));
}

export default {
    namespaced: true,

    state: {
        documentTypes: [],
        customerCategories: [],
        lists: {
            countries: [],
            counties: [],
            constituencies: [],
            wards: [],
            maritalStatuses: [],
            teams: [],
            branches: [],
            employees: [],
        },
    },

    getters: {
        documentTypes(state) {
            return state.documentTypes;
        },
        customerCategories(state) {
            return state.customerCategories;
        },
        lists(state) {
            return state.lists;
        },
    },

    mutations: {
        SET_DOCUMENT_TYPES(state, documentTypes) {
            return (state.documentTypes = documentTypes);
        },
        SET_CUSTOMER_CATEGORIES(state, categories) {
            return (state.customerCategories = categories);
        },
        SET_LIST(state, { key, rows }) {
            state.lists[key] = rows;
        },
    },

    actions: {
        // [{document_type_id, code, name, applies_to, handling, is_required,
        //   requires_number, number_label, number_format, requires_issue_date,
        //   requires_expiry_date, identity_group, sort_order, min_files,
        //   max_files}]. Filters: applies_to, is_group (0/1).
        async fetchDocumentTypes({ commit }, params = {}) {
            return await axios
                .get("/api/v1/lookups/document-types", { params: cleanQuery(params) })
                .then((res) => {
                    const list = Array.isArray(res.data?.data) ? res.data.data : [];
                    commit("SET_DOCUMENT_TYPES", list);
                    return list;
                })
                .catch((err) => Promise.reject(err));
        },

        // [{customer_category_id, name, is_group, status_id, subcategories[]}],
        // active subcategories only. Filter: is_group (0/1).
        async fetchCustomerCategories({ commit }, params = {}) {
            return await axios
                .get("/api/v1/lookups/customer-categories", { params: cleanQuery(params) })
                .then((res) => {
                    const tree = Array.isArray(res.data?.data) ? res.data.data : [];
                    commit("SET_CUSTOMER_CATEGORIES", tree);
                    return tree;
                })
                .catch((err) => Promise.reject(err));
        },

        // [{employee_id, full_name, branch_id}]: the active staff of the
        // company, by name. Needs no "view employee".
        async fetchEmployees({ commit }) {
            return await fetchList(commit, "employees", "/api/v1/lookups/employees", {});
        },

        // The legacy reference lists the member forms read (report=1 returns
        // every row), kept under `lists`, not in the legacy stores.
        async fetchCountries({ commit }) {
            return await fetchList(commit, "countries", "/api/v1/countries", { report: "1" });
        },
        async fetchCounties({ commit }, countryId) {
            return await fetchList(commit, "counties", "/api/v1/counties", { country_id: countryId, report: "1" });
        },
        async fetchConstituencies({ commit }, countyId) {
            return await fetchList(commit, "constituencies", "/api/v1/constituencies", {
                county_id: countyId,
                report: "1",
            });
        },
        async fetchWards({ commit }, constituencyId) {
            return await fetchList(commit, "wards", "/api/v1/wards", {
                constituency_id: constituencyId,
                report: "1",
            });
        },
        async fetchMaritalStatuses({ commit }) {
            return await fetchList(commit, "maritalStatuses", "/api/v1/maritalstatuses", { report: "1" });
        },
        async fetchTeams({ commit }) {
            return await fetchList(commit, "teams", "/api/v1/teams", { report: "1", relations: "1" });
        },
        async fetchBranches({ commit }) {
            return await fetchList(commit, "branches", "/api/v1/branches", { report: "1" });
        },
    },
};
